import { useEffect, useState } from "react";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getAvatarColor, getAvatarInitials, parseText } from "../../utils/utils";
import { Box, useTheme } from "@mui/system";
import InsightsMagicSvg from "../../assets/interaction-detail-page/selectedAsk7g_icon.svg";

import {
  Avatar,
  Tooltip,
  Typography,
} from "@mui/material";
import { getUserAttributes } from "../../utils/auth";
import FeedBackIcon from "../../assets/common/feedBackIcon.svg";
import FeedbackPopup from "../Common/FeedbackPopUp";
import useGlobalContext from "../../context/useGlobalContext";
import CopyIcon from "../../assets/interaction-detail-page/copy_icon.svg";
import { getKeyTakeawaysHTML } from "./copyTextFormatter";

interface AbstractiveSummaryPageProps {
  interactionId: string;
  insightsData: any;
}
type ScoreType = "Positive" | "Negative" | "Neutral" | "Mixed";
type InsightsSummary = {
  score: ScoreType;
  sentimentSummary: any;
  isAgendaPresent?: boolean; 

  summaryByTopic: {
    topic: string;
    summary: string;
    topicSentiment: string;
    topicClassification: string;
  }[];
  analysisTime: number;
  executiveReason: string;
  executiveAttentionNeeded: string;
  peopleInvolved?: string[];
  keyTakeaways?: {
    header: string;
    description: string;
  }[];
};

const AbstractiveSummaryPage = ({
  interactionId,
  insightsData,
}: AbstractiveSummaryPageProps) => {
  const { showSnackbar } = useDisplaySnackbar();
  const [insights, setInsights] = useState<InsightsSummary | null>(null);
  const theme = useTheme();
  const loggedInEmail = getUserAttributes()?.email;
  const hasSentiment = insights?.sentimentSummary?.trim();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState<{
    score?: string;
    executiveAttentionNeeded?: string;
    feedbackReference?: string;
  }>({});
  const { featureMap, impersonationDetails } = useGlobalContext();
  const userFeedback = featureMap?.idp_user_feedback === true;
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  const handleFeedbackClick = (type: "score" | "exec") => {
    setTooltipOpen(false);
    if (type === "score") {
      setFeedbackContext({
        score: insights?.score,
        feedbackReference: "How did the interaction go?",
      });
    }
    if (type === "exec") {
      setFeedbackContext({
        executiveAttentionNeeded: insights?.executiveAttentionNeeded,
        feedbackReference: "Exec Attention Needed?",
      });
    }
    setFeedbackOpen(true);
  };

  const GetInsightScore = (insights: any) => {
    let color = "";
    const score = insights?.insights?.score || "";
    switch (score) {
      case "Positive":
        color = "#32BD00";
        break;
      case "Negative":
        color = "#FF2E00";
        break;
      case "Neutral":
        color = "#FFC700";
        break;
      case "Mixed":
        color = "#127199";
        break;
      default:
        color = "#000000";
    }
    return (
      <Typography
        variant="body2"
        sx={{
          color: color,
          fontWeight: 600,
          marginTop: "4px",
          fontFamily: 'var(--font-family)',
          fontSize: "14px",
        }}
      >
        {score}
      </Typography>
    );
  };

  const people = insights?.peopleInvolved ?? [];

  useEffect(() => {
    if (insightsData) {
      const response = insightsData?.data?.[0];
      const sentiment = response?.sentiment
        ? response?.sentiment
        : response?.sentimentPayload;
      const summaryByTopic = response?.summaryByTopic?.summary_by_topic?.length
        ? response.summaryByTopic.summary_by_topic
        : response?.summaryByTopicPayload?.summary_by_topic || [];

      setInsights({
        score: sentiment?.overallSentiment || "",
        sentimentSummary:
          sentiment?.sentimentSummary &&
          parseText(sentiment?.sentimentSummary || ""),
        summaryByTopic: summaryByTopic || [],
        analysisTime: sentiment?.analysisTime || null,
        isAgendaPresent: response?.isAgendaPresent,
        executiveAttentionNeeded: response?.executiveAttentionNeeded,
        executiveReason: response?.executiveReason,
        peopleInvolved: response?.peopleInvolved|| [],
        keyTakeaways: response?.keyTakeaways || [],
      });
    }

  }, [insightsData]);

  const handleKeyTakeawaysCopy = async () => {
    const keyTakeaways = insights?.keyTakeaways ?? [];
    const html = getKeyTakeawaysHTML(keyTakeaways);
    if (!html) return;
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([html.replace(/<[^>]+>/g, "")], {
            type: "text/plain",
          }),
        }),
      ]);
      showSnackbar("success", "Key Takeaways copied to clipboard!");
    } catch {
      showSnackbar("error", "Failed to copy Key Takeaways.");
    }
  };
  return (
    <Box
      className="flex flex-col justify-start items-start gap-4 w-full pt-5"
      sx={{
        gap: "12px",
        [theme.breakpoints.up("lg")]: {
          gap: "10px",
        },
      }}
    >
      <Box className="insightsParent w-full">
        <Box
          className="flex flex-col justify-start items-start"
          sx={{
            background: "white",
            padding: "10px",
          }}
        >
          {insights && Object.keys(insights).length > 0 ? (
            <>
              {/* Conditional box for displaying time taken */}
              {loggedInEmail &&
                loggedInEmail.endsWith("@7thgear.ai") &&
                insights?.analysisTime && (
                  <Box className="flex flex-row justify-start items-center pb-4">
                    <img src={InsightsMagicSvg} />
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#BD00CD",
                        fontWeight: 500,
                        fontFamily: 'var(--font-family)',
                        fontSize: "16px",
                        paddingBottom: "10px",
                        paddingTop: "8px",
                        paddingLeft: "8px",
                      }}
                    >
                      It took{" "}
                      <span style={{ color: "#32BD00" }}>
                        {insights.analysisTime}
                      </span>{" "}
                      minute to generate the interaction analysis
                    </Typography>
                  </Box>
                )}
              {hasSentiment && (
              <>
              <Box className="flex flex-row items-center pb-4">
                <img src={InsightsMagicSvg} />
                <Typography
                  variant="h5"
                  sx={{
                    color: "#BD00CD",
                    fontFamily: 'var(--font-family)',
                    fontSize: "16px",
                    paddingLeft: "8px",
                    paddingBottom: "1px",
                  }}
                >
                  Was an agenda discussed at the beginning of the interaction ?
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    marginLeft: 1,
                    fontFamily: 'var(--font-family)',
                    fontSize: "16px",
                    color: insights.isAgendaPresent ? "#32BD00" : "#FF2E00",
                    fontWeight: 600,
                  }}
                >
                  {insights.isAgendaPresent ? "Yes" : "No"}
                </Typography>
              </Box>
              <Box className="flex flex-row justify-start items-center pb-2">
                <img src={InsightsMagicSvg}/>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#BD00CD",
                    fontFamily: 'var(--font-family)',
                    fontSize: "16px",
                    paddingLeft: "8px",
                  }}
                >
                  How did the interaction go?
                </Typography>
                <Box className="flex flex-row justify-start items-center ml-2 mb-1">
                <GetInsightScore insights={insights} />
                </Box>
                {userFeedback && 
                <Box sx={{marginLeft: 1}}>
                <Tooltip
                  title="Give feedback on this tag"
                  placement="top"
                  arrow
                  classes={{ tooltip: "custom-tooltip", arrow: "custom-tooltip-arrow" }}
                  open={tooltipOpen && !feedbackOpen}
                  onOpen={() => setTooltipOpen(true)}
                  onClose={() => setTooltipOpen(false)}
                >
                  <img
                    src={FeedBackIcon}
                    alt="feedback"
                    className="feedback-icon"
                    onClick={() => handleFeedbackClick("score")}
                  />
                </Tooltip>
                </Box>
                }
              </Box>
              </>
              )}
              {insights?.sentimentSummary ? (
                <>
                  <Typography
                    sx={{ fontFamily: 'var(--font-family)', fontSize: "14px", paddingLeft: "26px", paddingBottom: "16px", }}
                  >
                    {insights?.sentimentSummary}
                  </Typography>
                  <Box
                  >
                    <Box className= "insights_exective_reason_block">
                    {/* Second line: executive reason */}
                    {insights?.executiveReason && (
                      <Typography
                      sx={{ fontFamily: 'var(--font-family)', fontSize: "14px" }}
                      >
                        {insights?.executiveReason}
                      </Typography>
                    )}
                  </Box>
                  </Box>
                </>
              ) : (
                <Box className="flex flex-row justify-center items-center w-full">
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.primary.dark,
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    No sentiment analysis available.
                  </Typography>
                </Box>
              )}
              {(insights?.keyTakeaways ?? []).length > 0 && (
                  <Box className="key-takeaways-section">
                    <Box className="key-takeaways-header">
                      <img src={InsightsMagicSvg} />

                      <Typography className="summary-heading">
                        Key Takeaways
                      </Typography>
                      {!isImpersonating && 
                      <Tooltip placement="top" title="Copy Key Takeaways" arrow>
                        <img
                          src={CopyIcon}
                          alt="copy"
                          className="key-takeaways-copy-icon"
                          onClick={handleKeyTakeawaysCopy}
                        />
                      </Tooltip>
                      }
                    </Box>

                    <Box className="key-takeaways-list">
                      <ul className="key-takeaways-ul">
                        {insights.keyTakeaways!.map((item, index) => (
                          <li
                            key={index}
                            className="key-takeaway-item"
                          >
                            <Typography className="key-takeaway-text">
                              <span className="key-takeaway-title">
                                {item?.header}
                              </span>{" "}
                              - {item?.description}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  </Box>
                )}
              {people?.length > 0 && (
                <Box className="flex flex-col mt-3 w-full">
                  <Box className="flex flex-row items-center mb-2">
                    <img src={InsightsMagicSvg} />
                    <Typography
                      className="summary-heading"
                    >
                      People referenced on call:
                    </Typography>
                  </Box>

                  <Box className="summary-people-list">
                    {people.map((person, index) => (
                      <Box className="summary-people"
                        key={index}
                      >
                        <Avatar className="summary-avatar"
                          src={person || undefined}
                          alt={person}
                          sx={{
                            bgcolor: getAvatarColor(person),
                          }}
                        >
                          {!person && getAvatarInitials(person)}
                        </Avatar>

                        <Typography
                          sx={{
                            fontSize: "13px",
                          }}
                        >
                          {person}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box className="flex flex-row justify-center items-center w-full">
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: theme.palette.primary.dark }}
              >
                No insights available
              </Typography>
            </Box>
          )}
        </Box>
        <FeedbackPopup
          open={feedbackOpen}
          interactionId={interactionId || ""}
          feedbackKey="abstraction_insights"
          onClose={() => setFeedbackOpen(false)}
          onSubmit={() => {
            showSnackbar("success", "Feedback submitted successfully.");
          }}
          score={feedbackContext.score}
          executiveAttentionNeeded={feedbackContext.executiveAttentionNeeded}
          feedbackReference={feedbackContext.feedbackReference}
        />
      </Box>
    </Box>
  );
};

export default AbstractiveSummaryPage;
